/**
 * ToteScannerView — Tote barcode scanner view within the unified ScannerPage.
 *
 * Scans a tote barcode, resolves the toteContainerId, fetches items,
 * then navigates to ToteItemsPage.
 */

import React, { useCallback, useRef, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import BarcodeScanner from "src/components/pages/barcodeScanner/BarcodeScanner";
import ToteManualEntryPage from "./ToteManualEntryPage";
import ToteLoadingScreen from "./ToteLoadingScreen";
import { ScanModeToggle, ScanMode, ScannerTopOverlay, getCameraErrorMessage, ScanErrorSheet } from "./ScannerSharedComponents";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/store";
import { fetchToteItems } from "src/services/toteService";
import PillButton from "src/components/UI/PillButton";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";

interface ToteScannerViewProps {
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
}

const ToteScannerView: React.FC<ToteScannerViewProps> = ({ activeMode, onModeChange }) => {
  const navigate = useNavigate();
  const { activeSiteCode } = useAppSelector((state) => state.user);

  const [isScanning, setIsScanning] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [scannedToteId, setScannedToteId] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  // Separate from scanError so the message stays visible through the sheet's close animation.
  const [sheetOpen, setSheetOpen] = useState(false);

  // Ref to prevent concurrent scans
  const isProcessingRef = useRef(false);
  // Incrementing request ID to invalidate stale in-flight requests
  const requestIdRef = useRef(0);

  const dismissScanError = useCallback(() => {
    setSheetOpen(false);
    setIsScanning(true);
  }, []);

  // Stop the scanner on a hard camera failure so onError cannot loop.
  const handleCameraError = useCallback((err: any) => {
    setCameraUnavailable(true);
    setScanError(getCameraErrorMessage(err?.name || "", "tote barcode"));
    setSheetOpen(true);
    setIsScanning(false);
  }, []);

  const goToManualEntry = useCallback(() => {
    setSheetOpen(false);
    setShowManualEntry(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    if (cameraUnavailable) {
      setSheetOpen(false);
    } else {
      dismissScanError();
    }
  }, [cameraUnavailable, dismissScanError]);

  const handleToteScan = useCallback(
    async (scannableId: string) => {
      const requestId = ++requestIdRef.current;
      setScannedToteId(scannableId);
      setIsLoading(true);
      setScanError(null);

      try {
        const { items, toteContainerId } = await fetchToteItems(scannableId, activeSiteCode);

        if (requestId !== requestIdRef.current) return;

        navigate("/tote/items", {
          state: {
            scannableId,
            siteCode: activeSiteCode,
            toteContainerId,
            items,
          },
        });
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setCameraUnavailable(false);
        setScanError("We couldn't load this tote. Make sure the barcode is clear, or try again.");
        setSheetOpen(true);
        setIsScanning(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [navigate, activeSiteCode],
  );

  const handleScan = useCallback(
    (result: any) => {
      if (!result || result.length === 0) return;
      if (isProcessingRef.current) return;

      const value: string = result[0].rawValue?.trim();

      if (value) {
        isProcessingRef.current = true;
        setIsScanning(false);
        handleToteScan(value).finally(() => {
          isProcessingRef.current = false;
        });
      }
    },
    [handleToteScan],
  );

  if (isLoading) {
    return (
      <ToteLoadingScreen
        scannableId={scannedToteId}
        onBack={() => {
          requestIdRef.current++;
          isProcessingRef.current = false;
          setIsLoading(false);
          setIsScanning(true);
        }}
      />
    );
  }

  if (showManualEntry) {
    return (
      <ToteManualEntryPage
        onSwitchToScanner={() => {
          setShowManualEntry(false);
          setCameraUnavailable(false);
          setScanError(null);
          setSheetOpen(false);
          setIsScanning(true);
        }}
        onSubmit={(value) => {
          setShowManualEntry(false);
          setCameraUnavailable(false);
          setScanError(null);
          setSheetOpen(false);
          setIsScanning(false);
          handleToteScan(value);
        }}
      />
    );
  }

  return (
    <Column
      className="barcode-scanner-viewport pwa-fullscreen-viewport"
      backgroundColor="#3a3a3a"
      height="100vh"
      spacing="none"
    >
      {/* Top overlay */}
      <ScannerTopOverlay title="Tote Scan">
        <Row
          className="camera-pill-bg"
          width="300px"
          alignmentHorizontal="center"
          spacingInset="300"
        >
          <Text color="inverted">Scan the tote barcode</Text>
        </Row>
      </ScannerTopOverlay>

      {/* Camera feed — includes scanning frame and guidance text */}
      <BarcodeScanner isScanning={isScanning} onScan={handleScan} onError={handleCameraError} />

      {/* Bottom overlay */}
      <Column
        className="camera-overlay camera-overlay--bottom"
        spacingInset="450"
        alignmentHorizontal="center"
        spacing="400"
      >
        <ScanModeToggle activeMode={activeMode} onModeChange={onModeChange} />
        <PillButton
          text="Having Trouble? Try manual Entry"
          color="glass"
          onClick={() => setShowManualEntry(true)}
        />
      </Column>

      {/* Scan error / camera-unavailable pop-up */}
      <ScanErrorSheet
        open={sheetOpen}
        message={scanError}
        cameraUnavailable={cameraUnavailable}
        onClose={handleSheetClose}
        onManualEntry={goToManualEntry}
        onScanAnother={dismissScanError}
        manualEntryLabel="ENTER TOTE BARCODE MANUALLY"
        scanAnotherLabel="SCAN ANOTHER TOTE / BARCODE"
      />
    </Column>
  );
};

export default ToteScannerView;
